import { useState, useRef, useEffect } from 'react';
import { Interview, MAX_REVIEW_COUNT, MIN_REVIEW_MESSAGE_LENGTH, MAX_REVIEW_TURNS } from '@recruitai/shared';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useReviewChat } from '@/hooks/useReviewChat';
import { useReviewStatus } from '@/hooks/useReviewStatus';
import { MessageSquare, Send, Loader2, RefreshCw } from 'lucide-react';

interface ReviewChatProps {
  jobId: string;
  candidateId: string;
  interviewId: string;
  interview: Interview;
  userRole: string;
  onReviewComplete?: () => void;
}

export function ReviewChat({
  jobId,
  candidateId,
  interviewId,
  interview,
  userRole,
  onReviewComplete,
}: ReviewChatProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const canAccess = ['owner', 'admin', 'recruiter'].includes(userRole);
  const isDone = interview.status === 'done';
  const currentReviewCount = interview.reviewCount ?? 0;
  const limitReached = currentReviewCount >= MAX_REVIEW_COUNT;
  const isProcessing = interview.reviewStatus === 'processing';

  const {
    sessionId,
    messages,
    isLoading,
    turnCount,
    canGenerate,
    remainingReviews,
    startReview,
    sendMessage,
    generateReview,
  } = useReviewChat({ jobId, candidateId, interviewId, initialReviewCount: currentReviewCount });

  const [reviewTriggered, setReviewTriggered] = useState(false);

  const { status: reviewStatus, isPolling } = useReviewStatus({
    jobId,
    candidateId,
    interviewId,
    enabled: isProcessing || reviewTriggered,
  });

  // Auto-scroll messages and keep chat visible
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      // Keep the chat container in view
      setTimeout(() => {
        chatContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        textareaRef.current?.focus();
      }, 150);
    }
  }, [messages]);

  // Scroll to chat and focus textarea when session starts
  useEffect(() => {
    if (sessionId && isOpen) {
      // Small delay to let the DOM render the chat area
      setTimeout(() => {
        chatContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        textareaRef.current?.focus();
      }, 100);
    }
  }, [sessionId, isOpen]);

  // Notify parent when review completes
  useEffect(() => {
    if (reviewStatus === 'completed' && onReviewComplete) {
      onReviewComplete();
    }
  }, [reviewStatus, onReviewComplete]);

  if (!canAccess || !isDone) return null;

  const handleSend = async () => {
    if (inputText.trim().length >= MIN_REVIEW_MESSAGE_LENGTH) {
      const text = inputText;
      setInputText('');
      await sendMessage(text);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleGenerate = async () => {
    await generateReview();
    setReviewTriggered(true);
  };

  const disableReason = !isDone
    ? 'Entrevista ainda não foi avaliada'
    : limitReached
      ? 'Limite de revisões atingido'
      : isProcessing
        ? 'Revisão em andamento'
        : null;

  return (
    <Card className="p-4 space-y-3" ref={chatContainerRef}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          <h4 className="font-semibold text-sm">Revisar com IA</h4>
          {currentReviewCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {remainingReviews} de {MAX_REVIEW_COUNT} revisões disponíveis
            </Badge>
          )}
        </div>

        {!sessionId && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    variant="outline"
                    type="button"
                    onClick={() => { setIsOpen(true); startReview(); }}
                    disabled={!!disableReason || isLoading}
                  >
                    {isLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                    Iniciar Revisão
                  </Button>
                </span>
              </TooltipTrigger>
              {disableReason && <TooltipContent>{disableReason}</TooltipContent>}
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Processing indicator */}
      {(isProcessing || isPolling) && !sessionId && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-muted rounded-md">
          <Loader2 className="h-4 w-4 animate-spin" />
          A IA está reavaliando a entrevista...
        </div>
      )}

      {/* Chat area */}
      {isOpen && sessionId && (
        <div className="space-y-3">
          {/* Messages */}
          <div className="max-h-80 overflow-y-auto space-y-2 p-2 bg-muted/30 rounded-md">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg px-3 py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Turn counter */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{turnCount} de {MAX_REVIEW_TURNS} turnos usados</span>
            {canGenerate && <span className="text-green-600 font-medium">Pronto para gerar reavaliação</span>}
          </div>

          {/* Input area */}
          {turnCount < MAX_REVIEW_TURNS && !canGenerate && (
            <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-2">
              <Textarea
                ref={textareaRef}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Descreva sua observação sobre a avaliação..."
                className="min-h-[60px] resize-none text-sm"
                disabled={isLoading}
              />
              <Button
                size="sm"
                type="submit"
                disabled={isLoading || inputText.trim().length < MIN_REVIEW_MESSAGE_LENGTH}
                className="self-end"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          )}

          {/* Generate review button */}
          {canGenerate && (
            <Button
              type="button"
              onClick={handleGenerate}
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Gerar Reavaliação
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
